/**
 * Relationship slice — link creation mode and relationship management.
 *
 * Manages the interactive link creation workflow:
 * - Ctrl+Click: parent-child relationship (Task 4.3)
 * - Alt+Click: related/cross-axis link (Task 4.3b)
 *
 * WQ-340: every connection is written through the edge endpoints as a mirrored
 * pair of rows (parent-child = `child` on the parent + `parent` on the child;
 * related = `peer` both ways), then both nodes are re-fetched from the server.
 */

import type { StateCreator } from 'zustand';
import type { EdgeAttrs } from '../../../services/adjacency-map-api';
import { enqueue } from './persist-queue';
import type { AdjacencyStoreState } from './store-types';

export type LinkMode = 'parent-child' | 'related' | null;

const PARENT_CHILD_STYLE: EdgeAttrs = { lineStyle: 'solid', lineColor: '#3b82f6' };
const RELATED_STYLE: EdgeAttrs = { lineStyle: 'dashed', lineColor: '#f59e0b' };

export interface RelationshipSlice {
  /** Active link creation mode (keyboard modifier or toolbar toggle). */
  linkMode: LinkMode;

  /** Set the link creation mode. */
  setLinkMode: (mode: LinkMode) => void;

  /**
   * Create a parent-child relationship between two nodes.
   * parentId gets a 'child' edge, childId gets a 'parent' edge.
   */
  createParentChildLink: (parentId: string, childId: string) => Promise<void>;

  /**
   * Create a symmetric related link between two nodes.
   * Both nodes get a 'peer' edge.
   */
  createRelatedLink: (nodeA: string, nodeB: string) => Promise<void>;

  /**
   * Remove all edges between two nodes (both directions).
   */
  removeRelationship: (nodeA: string, nodeB: string) => Promise<void>;

  /**
   * Convert the edge type between two nodes.
   * Removes old edges and creates new ones with the correct type/style/color.
   * For parent-child, parentId determines direction.
   */
  convertEdgeType: (
    nodeA: string,
    nodeB: string,
    toType: 'parent-child' | 'related',
    parentId?: string,
  ) => Promise<void>;
}

export const createRelationshipSlice: StateCreator<
  AdjacencyStoreState,
  [],
  [],
  RelationshipSlice
> = (_set, get) => ({
  linkMode: null,

  setLinkMode: (mode) => _set({ linkMode: mode }),

  createParentChildLink: (parentId, childId) => enqueue(async () => {
    const parent = get().nodes.find((n) => n.id === parentId);
    if (parent?.edges.some((e) => e.targetId === childId && e.relationship === 'child')) return;
    get().pushHistory();
    try {
      await get().dataService.createEdgePair(parentId, childId, 'parent-child', PARENT_CHILD_STYLE);
      await get().refreshPair(parentId, childId);
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  createRelatedLink: (nodeA, nodeB) => enqueue(async () => {
    const a = get().nodes.find((n) => n.id === nodeA);
    if (a?.edges.some((e) => e.targetId === nodeB && e.relationship === 'peer')) return;
    get().pushHistory();
    try {
      await get().dataService.createEdgePair(nodeA, nodeB, 'peer', RELATED_STYLE);
      await get().refreshPair(nodeA, nodeB);
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  removeRelationship: (nodeA, nodeB) => enqueue(async () => {
    get().pushHistory();
    try {
      await get().dataService.deleteEdgesBetween(nodeA, nodeB);
      await get().refreshPair(nodeA, nodeB);
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  convertEdgeType: (nodeA, nodeB, toType, parentId) => enqueue(async () => {
    get().pushHistory();
    try {
      await get().dataService.deleteEdgesBetween(nodeA, nodeB);
      if (toType === 'parent-child') {
        const pId = parentId ?? nodeA;
        const cId = pId === nodeA ? nodeB : nodeA;
        await get().dataService.createEdgePair(pId, cId, 'parent-child', PARENT_CHILD_STYLE);
      } else {
        await get().dataService.createEdgePair(nodeA, nodeB, 'peer', RELATED_STYLE);
      }
      await get().refreshPair(nodeA, nodeB);
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),
});
